import React, { useLayoutEffect, useState } from 'react';

/**
 * The three files one banner photo ships as. WebP everywhere it's supported
 * (every browser since 2020), a narrow re-encode for phones so a 360px
 * screen doesn't pull the desktop-width original down a mobile connection,
 * and a plain JPEG/PNG last so the banner degrades to a photo rather than to
 * a flat colour on anything older.
 */
export interface HeroBannerImage {
  /** Wide WebP -- tablet and up. */
  webp: string;
  /** Narrow WebP served below 768px. Falls back to `webp` when absent. */
  webpMobile?: string;
  /** JPEG/PNG for browsers without WebP. */
  fallback: string;
  /**
   * Left empty on purpose for a decorative backdrop -- the headline laid
   * over it already says what the page is about, and a screen reader
   * announcing the photo a second time only adds noise. Set it only when
   * the picture carries information the copy doesn't.
   */
  alt?: string;
}

interface HeroBannerProps {
  image: HeroBannerImage;
  /** Banner height in px. Callers pick their own per breakpoint. */
  height: number;
  /**
   * CSS object-position for the photo. The default centres it, which on a
   * band shorter than the source crops top and bottom evenly -- override
   * when the part of the picture that matters isn't in the middle.
   */
  focalPoint?: string;
  /** Padding around the content layer -- the caller's own spacing, per breakpoint. */
  contentPadding?: string;
  /**
   * How much of the parent's own top padding to swallow, so the banner
   * starts flush against whatever sits above it instead of floating on a
   * strip of page background. 24 matches the `padding: "24px"` that both
   * AppLayout's and LandingLayout's Content use; pass 0 for a parent
   * without any.
   */
  pullUpTop?: number;
  /** Laid over the scrim -- headline, search bar, buttons. */
  children: React.ReactNode;
}

/**
 * A full-bleed photo band with a dark scrim and the caller's own content on
 * top of it. Used by the homepage hero; kept generic (the photo, the height,
 * the crop and the content all come in as props) so any other page that
 * wants the same treatment reuses this instead of rebuilding the breakout
 * and the gradient stack.
 *
 * "Full bleed" here means touching both edges of the viewport from inside a
 * padded, centred page container -- measured rather than done with `100vw`,
 * which counts the scrollbar and would leave the band a few pixels wider
 * than the page and pushed off-centre (index.css clips the overflow
 * globally, so it wouldn't even show as a scrollbar, just as a clipped right
 * edge). `document.documentElement.clientWidth` excludes the scrollbar, and
 * `calc((100% - Wpx) / 2)` as a negative-ish left margin re-centres the band
 * on its container -- exact, with no transform to blur the text over it.
 * That last step assumes the container is itself centred in the viewport,
 * which is what both app layouts do (symmetric padding on a centred flex
 * Content).
 */
const HeroBanner: React.FC<HeroBannerProps> = ({
  image,
  height,
  focalPoint = 'center',
  contentPadding = '48px 24px',
  pullUpTop = 24,
  children,
}) => {
  const [viewportWidth, setViewportWidth] = useState<number | null>(null);

  // useLayoutEffect, not useEffect: the width has to be known before the
  // first paint, or the banner renders one frame at container width and
  // then snaps out to the full viewport.
  useLayoutEffect(() => {
    const measure = () => setViewportWidth(document.documentElement.clientWidth);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const bleed: React.CSSProperties = viewportWidth
    ? { width: viewportWidth, marginLeft: `calc((100% - ${viewportWidth}px) / 2)` }
    : { width: '100%' };

  return (
    <section
      style={{
        position: 'relative',
        overflow: 'hidden',
        minHeight: height,
        marginTop: -pullUpTop,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Behind the photo, so the band is already the right darkness while
        // the image is still loading instead of flashing white under the
        // white headline.
        background: '#0B1512',
        ...bleed,
      }}
    >
      <picture className="hero-banner-frame">
        <source media="(max-width: 767px)" srcSet={image.webpMobile ?? image.webp} type="image/webp" />
        <source srcSet={image.webp} type="image/webp" />
        <img
          className="hero-banner-photo"
          src={image.fallback}
          alt={image.alt ?? ''}
          // This is the page's largest element and its LCP candidate --
          // never lazy, and asked for ahead of the rest of the page.
          loading="eager"
          // React 18's JSX types don't know this one yet; the all-lowercase
          // DOM attribute passes straight through, which is what we want.
          {...{ fetchpriority: 'high' }}
          style={{ objectPosition: focalPoint }}
        />
      </picture>

      {/* Scrim -- three stacked layers, painted top to bottom in the order
          they're written. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background:
            // A soft dark pool under the copy itself. Doing this locally is
            // what let the vertical layer below stay light enough for the
            // machines to keep their detail -- darkening the whole band
            // until the subtitle cleared the dust behind it turned the
            // photo into grey soup.
            'radial-gradient(70% 58% at 50% 42%, rgba(6,14,11,0.52), rgba(6,14,11,0) 72%), ' +
            // A breath of the app's teal, so the band belongs to this
            // palette instead of reading as a neutral stock photo.
            'radial-gradient(110% 80% at 50% 26%, rgba(29,158,117,0.12), rgba(29,158,117,0) 62%), ' +
            // Top: separates the band from the white header above it.
            // Bottom: where the photo is busiest and the search bar sits.
            'linear-gradient(180deg, rgba(8,18,14,0.60) 0%, rgba(8,18,14,0.34) 34%, rgba(8,18,14,0.50) 68%, rgba(8,18,14,0.78) 100%)',
          pointerEvents: 'none',
        }}
      />

      <div style={{ position: 'relative', zIndex: 1, width: '100%', padding: contentPadding }}>{children}</div>

      {/* No fade into the page at the bottom edge: it was tried and it
          washed a grey haze straight across the crew's faces, since the
          scrim is at its darkest exactly where the gradient had to reach
          white. A clean cut reads as deliberate; the smear read as a
          rendering fault. */}
    </section>
  );
};

export default HeroBanner;
